import { signQuery } from './sign';
import { getInitData, isInTelegram } from './telegram';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8201';
const API_SIGN_SECRET = process.env.REACT_APP_API_SIGN_SECRET || 'CHANGE_ME_TO_A_RANDOM_32+_BYTES_SECRET';

const TOKEN_STORAGE_KEY = 'tma_token';
const TOKEN_HEAD_STORAGE_KEY = 'tma_token_head';

type CommonResult<T> = {
  code: number;
  message: string;
  data: T;
};

export type CommonPage<T> = {
  current: number;
  size: number;
  pages: number;
  total: number;
  records: T[];
};

export class ApiError extends Error {
  code: number;

  constructor(code: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

export const getToken = (): string => window.localStorage.getItem(TOKEN_STORAGE_KEY) ?? '';

const getTokenHead = (): string =>
  window.localStorage.getItem(TOKEN_HEAD_STORAGE_KEY) ?? 'Bearer ';

const setAuth = (token: string, tokenHead: string): void => {
  window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  window.localStorage.setItem(TOKEN_HEAD_STORAGE_KEY, tokenHead);
};

const clearAuth = (): void => {
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  window.localStorage.removeItem(TOKEN_HEAD_STORAGE_KEY);
};

const buildAuthHeader = (): Record<string, string> => {
  const token = getToken();
  return token ? { Authorization: `${getTokenHead()}${token}` } : {};
};

const signUrl = async (url: string): Promise<string> => {
  if (!API_SIGN_SECRET) {
    return url;
  }
  const queryIndex = url.indexOf('?');
  const base = queryIndex >= 0 ? url.slice(0, queryIndex) : url;
  const params: Record<string, string> = {};
  if (queryIndex >= 0) {
    new URLSearchParams(url.slice(queryIndex + 1)).forEach((value, key) => {
      params[key] = value;
    });
  }
  const signed = await signQuery(params, API_SIGN_SECRET);
  return `${base}?${new URLSearchParams(signed).toString()}`;
};

export const loginWithTelegram = async (): Promise<void> => {
  const initData = getInitData();
  if (!initData) {
    throw new ApiError(401, '请在 Telegram 中打开本页面');
  }
  const url = await signUrl(`${API_BASE_URL}/admin/tg/login`);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ initData }),
  });
  if (!response.ok) {
    throw new Error(`请求失败 (${response.status})`);
  }
  const body = (await response.json()) as CommonResult<{ token: string; tokenHead: string }>;
  if (body.code !== 200) {
    throw new ApiError(body.code, body.message || '登录失败');
  }
  setAuth(body.data.token, body.data.tokenHead || 'Bearer ');
};

export const loginWithPassword = async (username: string, password: string): Promise<void> => {
  const url = await signUrl(`${API_BASE_URL}/admin/admin/loginWithCaptcha`);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) {
    throw new Error(`请求失败 (${response.status})`);
  }
  const body = (await response.json()) as CommonResult<{ token: string; tokenHead: string }>;
  if (body.code !== 200) {
    throw new ApiError(body.code, body.message || '登录失败');
  }
  setAuth(body.data.token, body.data.tokenHead || 'Bearer ');
};

let reloginPromise: Promise<void> | null = null;

const ensureLogin = async (): Promise<void> => {
  if (getToken()) {
    return;
  }
  if (!isInTelegram()) {
    throw new ApiError(401, '未登录');
  }
  if (!reloginPromise) {
    reloginPromise = loginWithTelegram().finally(() => {
      reloginPromise = null;
    });
  }
  return reloginPromise;
};

type RequestOptions = {
  method?: 'GET' | 'POST';
  body?: unknown;
};

const requestRaw = async <T>(path: string, options: RequestOptions): Promise<CommonResult<T>> => {
  const url = await signUrl(`${API_BASE_URL}${path}`);
  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers: {
      ...buildAuthHeader(),
      ...options.body !== undefined ? { 'Content-Type': 'application/json' } : {},
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });
  if (!response.ok) {
    throw new Error(`请求失败 (${response.status})`);
  }
  const body = (await response.json()) as CommonResult<T>;
  return body;
};

const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  await ensureLogin();
  let body = await requestRaw<T>(path, options);
  if (body.code === 401) {
    clearAuth();
    if (isInTelegram()) {
      await loginWithTelegram();
      body = await requestRaw<T>(path, options);
    } else {
      throw new ApiError(401, '登录已过期，请重新登录');
    }
  }
  if (body.code !== 200) {
    throw new ApiError(body.code, body.message || '请求失败');
  }
  return body.data;
};

export const logout = async (): Promise<void> => {
  if (getToken()) {
    try {
      await requestRaw('/admin/admin/logout', { method: 'GET' });
    } catch {
      // 忽略退出接口异常，本地清除登录态即可
    }
  }
  clearAuth();
};

export const get = async <T>(path: string): Promise<T> => request<T>(path);

export const getPage = async <T>(path: string): Promise<CommonPage<T>> =>
  get<CommonPage<T>>(path);

export const post = async <T>(path: string, data: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: data });
